import React, { useMemo, useCallback, useState, useRef } from 'react';
import { Feather } from '@expo/vector-icons';
import { Pressable, Text, View, StyleSheet, useWindowDimensions } from 'react-native';
import { HeaderAvatarButton } from '../../../HeaderAvatarButton';
import { useRouter } from 'expo-router';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Paprika, PaprikaLayout } from '../../../../styles/theme';
import { FAN_REASONS, MEALS, SIDE_REASONS, slotKey, rowOf, dayOf, dayFullKey, shiftWeek, STAG_MS_MOBILE } from '../constants';
import { Toast } from '../primitives/Toast';
import { DayList, type DayListSection } from './DayList';
import { EntryRow } from './EntryRow';
import { RecipeDetailSheet } from './RecipeDetailSheet';
import { CookCapture, type CookCaptureEntry } from '../../../recipe-detail/CookLog';
import { useTranslation } from 'react-i18next';
import { useSameMealExclude } from '../../../../hooks/useSameMealExclude';
import { useSlotFit } from '../../../../hooks/useSlotFit';
import { usePrewarmPantryMatcher } from '../../../../hooks/usePrewarmPantryMatcher';
import { usePrefetchPlannedRecipes } from '../../../../hooks/usePrefetchPlannedRecipes';
import { prefetchRecipe } from '../../../../services/recipePrefetch';
import { DISQUALIFYING_SCORE } from '../../../../services/planRanking';
import { useHouseholdMembers } from '../../../../hooks/useHouseholdMembers';
import { slotHasAcceptedMeal } from '../../proposalState';
import { FanSheet, FanOptionM } from './FanSheet';
import { LibrarySheet } from './LibrarySheet';
import { ChooserSheet } from './ChooserSheet';
import { EntryFormSheet } from './EntryFormSheet';
import { LeftoverSheet } from './LeftoverSheet';
import { useUndoableWeek } from '../useUndoableWeek';
import {
  useGenerateWeek, fitsMealType, openGenerationSelections, cookedSlotsOf, firstRegeneratedDay,
} from '../../../meal-config/useGenerateWeek';
import { generationSlotsMap } from '../../../meal-config/generationPlan';
import type { LeftoverCandidate } from '../leftovers';
import {
  useLeftoverCandidates,
  usePendingCapture,
  usePendingLeftovers,
  todayIso,
} from '../useLeftoverCandidates';
import { useLeftoverPlacement } from '../useLeftoverPlacement';
import { LeftoverCapturePrompt } from '../LeftoverCapturePrompt';
import { LeftoverPendingBanner } from '../LeftoverPendingBanner';
import { entryTitle, entrySub, isEntryMeal, isExclusiveEntryMeal, ENTRY_RECIPE_ID, isRecipeMeal } from '../../../../redux/mealEntry';
import { backfillIndexFromMeals, localizeIndex } from '../mealIndexFallback';
import { selectCurrentLanguage } from '../../../../redux/languageSlice';
import { buildSlotMeals, canAddDish, isAccepted, primarySlotMap, slotKeyOfMeal, MI_BY_MEAL_TYPE } from '../slotMeals';
import { useSideSuggestions } from '../useSideSuggestions';
import { useSideFoodPicks } from '../sideFoodsState';
import { trackEvent } from '../../../../services/analyticsService';
import { findHeroTarget, nowAnchor } from '../nextMeal';
import { placedInSlotMessage, slotLabel } from '../slotPlacementToast';
import { addDays, dayIndexInWeek, weekStartOf, parseIsoDate, currentWeekStart as currentMonday } from '../../../../utils/localDate';
import { NextMealHero } from './NextMealHero';
import { ExtendPlanCard } from './ExtendPlanCard';
import { GroceryAskCard } from './GroceryAskCard';
import { ARRIVAL_RISE_MS } from './PendingDinnerRow';
import {
  advanceArrival, arrivalDone, emptyEvenings, landArrival, startArrival, type PlanArrival,
} from './planArrival';
import { useReduceMotion } from '../../../../hooks/useReduceMotion';
import { afterPaint } from '../../../../utils/afterPaint';
import { HORIZON_DAYS } from '../../../../services/horizonDays';
import { buildWeekSections, mergeFillOnly } from '../planHorizon';
import { plannerOpening } from '../plannerOpening';
import { WeekEmptyState } from './WeekEmptyState';
import { buildMovePlan, dayIndexOf, type MoveAction } from '../../../../services/mealMovePlan';
import { AddToMealPlanModal } from '../../../recipe-detail/AddToMealPlanModal';
import { openRecipeDetail } from '../../../../utils/recipeNavigation';
import { PortionsSheet } from './PortionsSheet';
import {
  setSel,
  setMoving,
  setLibFor,
  setDetailFor,
  clearToast,
  setConfigPhase,
  selectSel,
  selectMoving,
  selectLibFor,
  selectDetailFor,
  selectToast,
  selectConfigWeekPortions,
  setToast,
} from '../../../../redux/mealPlanUISlice';
import { buildGroceryList } from '../../../../redux/groceryListSlice';
import {
  selectCurrentWeekMeals,
  selectCurrentWeekStart,
  setCurrentWeek,
  setWeeklyPlan,
  removeFromWeek,
  addToWeek,
  swapSlots,
  moveMeal,
  getWeekStartForDate,
  selectMealsForDateRange,
  setSlotEntry,
  clearSlot,
  updateMealServings,
  markMealAsCooked,
  unmarkMealAsCooked,
  rateMeal,
  proposeMeal,
  type PlannedMeal,
} from '../../../../redux/mealPlanSlice';
import { selectRecipeIndex } from '../../../../redux/recipeIndexSlice';
import type { RootState } from '../../../../redux/store';
import { selectActiveInventory } from '../../../../redux/inventorySlice';
import { useCookPreferences } from '../../../../hooks/useCookPreferences';
import { selectAllRecipeMetadata } from '../../../../redux/recipeMetadataSlice';
import { useFanChoice, type FanChoiceParams } from '../useFanChoice';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import type { DietaryRole } from '../../../../redux/mealPlanSlice';
import { formatWeekRange, formatDayMonth, formatWeekdayShort } from '../../../../utils/dateLocale';




interface WeeklyMobileProps {
  /**
   * The full seven-day generator (`TheWeeklyScreen`'s `handleGenerateMVP`).
   * Deliberately not `useGenerateWeek`: that one fills slot keys inside one
   * Monday week, so it cannot reach past Sunday, and the empty state is exactly
   * where a cook most needs a whole week of food rather than the tail of one.
   */
  onGenerateWeek?: (weekStart?: string) => void;
  /**
   * "Planifier les 7 jours suivants" (#722). Generates seven dinners from
   * `startDate` and hands them to `commit`, which writes them fill-only under
   * an undo and returns the ones that went in.
   */
  onExtendPlan?: (startDate: string, commit: (meals: PlannedMeal[]) => PlannedMeal[]) => Promise<PlannedMeal[]>;
  isGenerating?: boolean;
}

const NO_MEALS: PlannedMeal[] = [];

export function WeeklyMobile({ onGenerateWeek, onExtendPlan, isGenerating }: WeeklyMobileProps = {}) {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const { status: householdStatus, myUid } = useHouseholdMembers();
  const { width } = useWindowDimensions();
  const compactHeader = width < 380;

  // Cook-capture sheet — the PlannedMeal the user is rating/noting (null = closed)
  const [cookCaptureMeal, setCookCaptureMeal] = useState<PlannedMeal | null>(null);

  const weekMeals = useSelector(selectCurrentWeekMeals);
  const weekStart = useSelector(selectCurrentWeekStart);
  const allWeeks = useSelector((state: RootState) => state.mealPlan.weeks);
  const recipeIndexState = useSelector(selectRecipeIndex);
  const language = useSelector(selectCurrentLanguage);
  const activeInventory = useSelector(selectActiveInventory);
  const prefs = useCookPreferences();
  const recipeMetadata = useSelector(selectAllRecipeMetadata);

  const sel = useSelector(selectSel);
  const moving = useSelector(selectMoving);
  const libFor = useSelector(selectLibFor);
  const detailFor = useSelector(selectDetailFor);
  const toast = useSelector(selectToast);
  const weekDefaultPortions = useSelector(selectConfigWeekPortions);

  // Local state for sheets
  const [chooser, setChooser] = useState<{ sk: number } | null>(null);
  /** The library was opened from an entry's menu (« Recette »): its pick replaces the entry. */
  const libReplacesEntry = useRef(false);
  const [entryForm, setEntryForm] = useState<{
    sk: number;
    type: 'dining' | 'note';
    initial?: { place?: string; note?: string; text?: string } | null;
  } | null>(null);
  const [leftoverPick, setLeftoverPick] = useState<{ sk: number } | null>(null);
  const [portionsFor, setPortionsFor] = useState<{ mealId: string } | null>(null);

  // The dish the move sheet is relocating. `fromFan` remembers whether the
  // sheet was opened from inside the fan (back returns there) or straight from
  // the card / detail sheet (back closes the flow) — #563 added those two
  // direct entry points because the fan-only one was undiscoverable.
  const [moveCtx, setMoveCtx] = useState<{ mealId: string; fromFan: boolean } | null>(null);

  // Which dish the fan sheet will swap out. null means the fan is adding a dish
  // (empty slot, or "Ajouter un plat" on a slot that already holds one).
  const [replaceTargetId, setReplaceTargetId] = useState<string | null>(null);
  // The fan is listing accompaniments for the slot's dish (#746), not dinners.
  const [fanSides, setFanSides] = useState(false);
  // Which dish the detail sheet was opened from — a recipe can sit in more than
  // one slot, so the recipeId alone doesn't identify the meal.
  const [detailMealId, setDetailMealId] = useState<string | null>(null);
  // The hero's offered side, opened from its name (#854). Kept apart from
  // detailFor: that sheet acts on a planned meal, and this recipe is not one yet.
  const [sidePreviewId, setSidePreviewId] = useState<string | null>(null);

  // Undo hook
  const { mutateWithUndo, undo } = useUndoableWeek();

  const today = todayIso();
  const thisMonday = currentMonday();
  const browsingPast = weekStart < thisMonday;

  // Régénérer covers the seven days the strip shows, from today (#808), across
  // two Monday buckets when they span one. Browsing the past it stays on the
  // page's week, where nothing is open and the toast says so.
  const regenerateFrom = browsingPast ? undefined : today;
  const { generate: generateWeek } = useGenerateWeek({ windowStart: regenerateFrom });

  /**
   * Every meal (accepted and proposed) filed by the Monday of its own date.
   * Read by date rather than by bucket: historical writes filed some meals
   * under a bucket that does not match their date, and the list shows them.
   */
  const mealsByWeek = useMemo(() => {
    const map = new Map<string, PlannedMeal[]>();
    for (const meal of Object.values(allWeeks).flat()) {
      const monday = weekStartOf(meal.scheduledFor);
      const list = map.get(monday);
      if (list) list.push(meal);
      else map.set(monday, [meal]);
    }
    return map;
  }, [allWeeks]);
  const mealsOfWeek = useCallback((monday: string) => mealsByWeek.get(monday) ?? NO_MEALS, [mealsByWeek]);

  /** The meals of Régénérer's own seven days (#808). */
  const regenerateWindowMeals = useMemo(() => {
    if (!regenerateFrom) return NO_MEALS;
    const until = addDays(regenerateFrom, 6);
    return [...new Set([weekStartOf(regenerateFrom), weekStartOf(until)])]
      .flatMap(mealsOfWeek)
      .filter((m) => m.scheduledFor >= regenerateFrom && m.scheduledFor <= until);
  }, [mealsOfWeek, regenerateFrom]);
  const showRegenerate = weekMeals.length > 0 || regenerateWindowMeals.length > 0;
  // « Régénère : ven 30 → mer 4 » — the first day it writes, so tonight kept (#862)
  // is not announced as covered, and the button no longer means "this Monday week".
  const regenerateRange = useMemo(() => {
    if (!regenerateFrom) return null;
    const from = firstRegeneratedDay(
      openGenerationSelections(
        [],
        generationSlotsMap(regenerateWindowMeals, regenerateFrom),
        regenerateFrom,
        nowAnchor(),
        cookedSlotsOf(regenerateWindowMeals, regenerateFrom),
      ),
      regenerateFrom,
    );
    const until = addDays(regenerateFrom, 6);
    return {
      from: `${formatWeekdayShort(from, i18n.language)} ${parseIsoDate(from).getDate()}`,
      to: `${formatWeekdayShort(until, i18n.language)} ${parseIsoDate(until).getDate()}`,
    };
  }, [regenerateFrom, regenerateWindowMeals, i18n.language]);

  /**
   * Weeks the cook has scrolled into past the plan (#769): reaching the end of
   * the list adds the next one. Back to none when the page changes week.
   */
  const [loadedWeeks, setLoadedWeeks] = useState(0);
  React.useEffect(() => { setLoadedWeeks(0); }, [weekStart]);

  /**
   * The weeks the one scroll stacks (#722): this page's week, then every week
   * after it through the plan and the strip, each drawn whole, plus the weeks
   * scrolled into. See `buildWeekSections`.
   */
  const { sections: sectionPlan, extendFrom, canLoadMore } = useMemo(
    () => buildWeekSections(weekStart, today, mealsOfWeek, loadedWeeks),
    [weekStart, today, mealsOfWeek, loadedWeeks],
  );
  const loadNextWeek = useCallback(() => setLoadedWeeks((n) => n + 1), []);
  const sectionMeals = useMemo(
    () => sectionPlan.flatMap((section) => mealsOfWeek(section.weekStart)),
    [sectionPlan, mealsOfWeek],
  );
  // Every meal of a slot, primary dish first — a slot can legitimately hold up
  // to MAX_MEALS_PER_SLOT recipes (main + side). Proposals included, so they
  // render in the list.
  const sections = useMemo<DayListSection[]>(
    () => sectionPlan.map((section) => ({
      ...section,
      slotMeals: buildSlotMeals(mealsOfWeek(section.weekStart)),
    })),
    [sectionPlan, mealsOfWeek],
  );
  const pageSlotMeals = sections[0].slotMeals;
  /** Accepted meals anywhere in the scroll — what id lookups resolve against. */
  const visibleMeals = useMemo(() => sectionMeals.filter((m) => m.mode !== 'proposed'), [sectionMeals]);

  /**
   * The week the cook last touched. Slot keys are relative to a Monday, and
   * the scroll now holds several: every sheet that acts on a slot resolves its
   * date against this week, set by the row that opened it.
   */
  const [activeWeek, setActiveWeek] = useState(weekStart);
  React.useEffect(() => { setActiveWeek(weekStart); }, [weekStart]);
  const slotMeals = useMemo(
    () => sections.find((section) => section.weekStart === activeWeek)?.slotMeals
      ?? buildSlotMeals(mealsOfWeek(activeWeek)),
    [sections, activeWeek, mealsOfWeek],
  );

  // Undo restores the week the edit landed in, not only the page's week.
  const mutate = useCallback(
    (message: string, mutator: () => void, alsoWeeks: string[] = []) =>
      mutateWithUndo(message, mutator, [activeWeek, ...alsoWeeks]),
    [mutateWithUndo, activeWeek],
  );

  const recipeIndex = useMemo<Record<string, RecipeIndexEntry>>(() => {
    const map: Record<string, RecipeIndexEntry> = {};
    recipeIndexState.forEach((r: RecipeIndexEntry) => { map[r.id] = r; });
    // Meals whose recipe isn't in the index would render as empty slots —
    // fall back to the meal's denormalized recipe so the card stays visible.
    // Proposals included so they don't drop out of the index.
    return localizeIndex(backfillIndexFromMeals(map, sectionMeals), sectionMeals, language);
  }, [recipeIndexState, sectionMeals, language]);

  // Primary dish per slot, for the flows that still act one meal at a time
  // (move/swap, leftovers).
  const slotsMap = useMemo<Map<number, PlannedMeal>>(() => primarySlotMap(slotMeals), [slotMeals]);

  // Leftovers are chosen by date, not by position in the grid on screen (#663).
  const leftoverTargetDate = leftoverPick ? addDays(activeWeek, dayOf(leftoverPick.sk)) : null;
  const { fridge: leftoverFridge, freezer: leftoverFreezer } =
    useLeftoverCandidates(leftoverTargetDate);
  const placeLeftover = useLeftoverPlacement(t);
  const captureMeal = usePendingCapture(today);
  const pendingLeftovers = usePendingLeftovers(today);

  const recipeEntries = useMemo<RecipeIndexEntry[]>(() => recipeIndexState, [recipeIndexState]);

  /**
   * The dish the hero card answers with (#579): the next one still ahead today
   * (#828). Recomputed whenever the week changes, so marking lunch cooked
   * promotes tonight's dinner on the spot. Null when the week on screen isn't
   * the current one or nothing is left today: the hero hides, and tonight's
   * « Ajouter un souper » is the first thing on the page.
   */
  const nextMeal = useMemo(() => {
    const { today, nowMinutes } = nowAnchor();
    const target = findHeroTarget({
      slotMeals: pageSlotMeals,
      weekStart,
      today,
      nowMinutes,
      resolves: (recipeId) => !!recipeIndex[recipeId],
    });
    if (!target) return null;

    const todayDi = dayIndexInWeek(weekStart, today);
    if (todayDi === null) return null;

    return { ...target, recipe: recipeIndex[target.meal.recipeId], todayDi };
  }, [pageSlotMeals, recipeIndex, weekStart]);

  /**
   * Meal rows the library simply cannot fill — no recipe of that meal type left
   * once the week's picks are excluded (#528: breakfast, typically). Derived
   * rather than stored, so it stays true after any number of regenerations and
   * heals by itself as soon as the user adds a matching recipe.
   */
  const exhaustedMealTypes = useMemo<Set<number>>(() => {
    const used = new Set(weekMeals.map((m) => String(m.recipeId)));
    const exhausted = new Set<number>();
    [0, 1, 2].forEach((mi) => {
      const hasOne = recipeEntries.some((r) => !used.has(r.id) && fitsMealType(r, mi));
      if (!hasOne) exhausted.add(mi);
    });
    return exhausted;
  }, [recipeEntries, weekMeals]);

  // New seed each time the sheet opens on a slot → fresh suggestions per
  // opening, stable while open. Derived during render so the first paint
  // already uses it.
  const sameMealExclude = useSameMealExclude();
  const slotFitOf = useSlotFit();
  const fanSeedRef = useRef(0);
  const prevSelRef = useRef<number | null>(null);
  if (sel !== prevSelRef.current) {
    prevSelRef.current = sel;
    if (sel !== null) fanSeedRef.current = Date.now();
  }
  const fanSeed = fanSeedRef.current;

  // The dish the fan sheet is swapping out (undefined while adding one).
  const fanTargetMeal = useMemo(
    () => (replaceTargetId ? visibleMeals.find((m) => m.id === replaceTargetId) : undefined),
    [replaceTargetId, visibleMeals]
  );
  // What the ranking reads of the week, as values: every live-sync snapshot
  // recreates the meal objects, and a ranking keyed on them re-ran the whole
  // library × pantry pass on each echo while the sheet was open (#865).
  const fanTargetId = fanTargetMeal?.id;
  const fanTargetRecipeId = fanTargetMeal?.recipeId;
  const placedKey = useMemo(() => {
    const ids: string[] = [];
    slotMeals.forEach((meals) => meals.forEach((m) => ids.push(m.recipeId)));
    return ids.join('\u0000');
  }, [slotMeals]);

  // The library × shelf walk every ranking below reads, done off the tap (#865).
  const historyRecipes = useMemo(
    () => visibleMeals.flatMap((m) => (isRecipeMeal(m) && m.recipe?.title ? [m.recipe] : [])),
    [visibleMeals],
  );
  usePrewarmPantryMatcher(recipeEntries, activeInventory, { history: historyRecipes, score: prefs.score });
  // Today's and tomorrow's recipes, read before they are opened (#874).
  usePrefetchPlannedRecipes(visibleMeals);
  const sideSuggester = useSideSuggestions(recipeIndex);
  const { forSlot: sidesForSlot, topForSlot: topSideForSlot } = sideSuggester;
  // The foods the cook asked the accompaniment list to use (#853).
  const { sideFoods, withFoods } = useSideFoodPicks({
    suggester: sideSuggester,
    slot: sel !== null ? slotMeals.get(sel) : undefined,
    active: fanSides && sel !== null,
    openingKey: fanSeed,
    surface: 'mobile_sheet',
  });

  // What the fan may offer this slot. Null while it lists accompaniments, whose
  // own list lives in `sideSuggester`.
  // Keyed on what the ranking reads, as values (#865): a live-sync snapshot
  // recreates every meal object, and params keyed on them re-ranked the whole
  // library on each echo while the sheet was open.
  const fanParams = useMemo<FanChoiceParams | null>(() => {
    if (sel === null || fanSides) return null;
    const placedRecipeIds = placedKey ? placedKey.split('\u0000') : [];
    const slotDate = addDays(activeWeek, dayOf(sel));
    const repeats = sameMealExclude(slotDate, fanTargetId);
    return {
      sk: sel,
      currentRecipeId: fanTargetRecipeId,
      allRecipes: recipeEntries,
      placedRecipeIds,
      inventory: activeInventory,
      metadataById: recipeMetadata,
      // Same hard rules the week generator obeys — allergies, excluded
      // ingredients and declared diets, from the one resolver.
      // ...and never a dish planned within two weeks of this day (#736).
      exclude: (r) => (prefs.hasHardRules && prefs.isExcluded(r)) || repeats(r),
      // ...and, for a dinner, the day's kind and time cap and the week's
      // balance — the rules the generators placed the week by.
      slotFit: rowOf(sel) === 2 ? slotFitOf(slotDate, fanTargetId) : undefined,
      viable: rowOf(sel) === 2
        ? (r) => prefs.score(r, { isWeekend: dayOf(sel) >= 5 }) > DISQUALIFYING_SCORE
        : undefined,
    };
  }, [sel, fanSides, recipeEntries, placedKey, fanTargetId, fanTargetRecipeId, activeInventory, recipeMetadata, prefs, sameMealExclude, slotFitOf, activeWeek]);
  const fanChoice = useFanChoice({ params: fanParams, openingKey: fanSeed, surface: 'mobile_sheet' });
  const { rows: fanRows, trackPick: trackFanPick } = fanChoice;

  const fanOptions = useMemo<FanOptionM[]>(() => {
    if (sel === null) return [];
    if (fanSides && withFoods) {
      return withFoods.options.map((s) => ({
        key: `side-food-m-${sel}-${s.recipe.id}`,
        recipe: s.recipe,
        consequence: t(s.reasonKey, s.reasonParams),
        reasonIdx: 0,
        reason: { accent: SIDE_REASONS[0].accent },
      }));
    }
    if (fanSides) {
      return sidesForSlot(slotMeals.get(sel), mealsOfWeek(activeWeek), fanSeed).map((s) => ({
        key: `side-m-${sel}-${s.recipe.id}`,
        recipe: s.recipe,
        consequence: t(s.reasonKey, s.reasonParams),
        reasonIdx: 0,
        reason: { labelKey: s.labelKey, accent: s.accent },
      }));
    }
    return fanRows.map((row) => ({
      key: `fan-m-${sel}-${row.recipe.id}`,
      recipe: row.recipe,
      consequence: row.reasonKey ? t(row.reasonKey, row.reasonParams) : undefined,
      reasonIdx: Math.max(0, FAN_REASONS.findIndex((r) => r.key === row.angleKey)),
      reason: row.accent ? { accent: row.accent } : undefined,
    }));
  }, [sel, fanSides, withFoods, sidesForSlot, mealsOfWeek, fanSeed, slotMeals, fanRows, activeWeek, t]);

  /**
   * What the hero says is served with tonight's dish (#746): the sides already
   * on the menu, or — while there is none — one accompaniment to offer.
   */
  const heroSk = nextMeal ? slotKey(nextMeal.mi, nextMeal.di) : null;
  const heroExtras = useMemo(() => {
    if (!nextMeal || heroSk === null) return [];
    return (pageSlotMeals.get(heroSk) ?? [])
      .filter((m) => isAccepted(m) && m.id !== nextMeal.meal.id && !!recipeIndex[m.recipeId])
      .map((m) => ({ mealId: m.id, recipe: recipeIndex[m.recipeId] }));
  }, [nextMeal, heroSk, pageSlotMeals, recipeIndex]);
  const heroSide = useMemo(
    () => (heroSk !== null && heroExtras.length === 0
      ? topSideForSlot(pageSlotMeals.get(heroSk), mealsOfWeek(weekStart))
      : null),
    [heroSk, heroExtras, topSideForSlot, pageSlotMeals, mealsOfWeek, weekStart],
  );

  const moveSourceMeal = useMemo(
    () => (moveCtx ? visibleMeals.find((m) => m.id === moveCtx.mealId) ?? null : null),
    [moveCtx, visibleMeals]
  );

  const moveTitle = useMemo(
    () => (moveSourceMeal && recipeIndex[moveSourceMeal.recipeId]?.title) || t('thisWeek.thisMeal'),
    [moveSourceMeal, recipeIndex, t]
  );

  const detailRecipe = detailFor ? (recipeIndex[detailFor] ?? null) : null;
  // The meal's sheet is open: « Voir la recette » is one tap away (#874).
  React.useEffect(() => prefetchRecipe(detailFor), [detailFor]);
  const sidePreview = sidePreviewId ? (recipeIndex[sidePreviewId] ?? null) : null;
  const detailMeal = detailFor
    ? (visibleMeals.find((m) => m.id === detailMealId) ?? visibleMeals.find((m) => m.recipeId === detailFor) ?? null)
    : null;

  const fanMealLabel = sel !== null
    ? t((MEALS.find((m, mi) => slotKey(mi, dayOf(sel)) === sel) ?? MEALS[rowOf(sel)])?.labelKey ?? '')
    : '';

  /**
   * Opens the fan sheet. mealId = the dish to swap out, null = add a dish.
   * `sides` lists accompaniments for the dish already there instead of dinners.
   */
  const openFan = useCallback((sk: number, mealId: string | null, sides = false) => {
    setReplaceTargetId(mealId);
    setFanSides(sides);
    dispatch(setSel(sk));
  }, [dispatch]);

  const closeFan = useCallback(() => {
    setReplaceTargetId(null);
    setFanSides(false);
    dispatch(setSel(null));
  }, [dispatch]);

  const handleDetailPress = useCallback((meal: PlannedMeal) => {
    // The sheet's shuffle and move act on this dish's slot, which is relative
    // to the dish's own Monday.
    setActiveWeek(weekStartOf(meal.scheduledFor));
    setDetailMealId(meal.id);
    dispatch(setDetailFor(meal.recipeId));
  }, [dispatch]);

  const handleReplacePress = useCallback((sk: number, meal: PlannedMeal) => {
    openFan(sk, meal.id);
  }, [openFan]);

  const handleAddDishPress = useCallback((sk: number) => {
    openFan(sk, null);
  }, [openFan]);

  /** « + avec ça » on a card, and the add row under a slot's sides (#746). */
  const handleAddSidePress = useCallback((sk: number) => {
    openFan(sk, null, true);
  }, [openFan]);

  const handleAddPress = useCallback((sk: number) => {
    // "+" on an empty slot opens the 5-suggestion fan directly; entry types
    // (Resto/Restes/Note/Rien) are secondary actions inside the fan.
    openFan(sk, null);
  }, [openFan]);

  const handleRemoveExtra = useCallback((meal: PlannedMeal) => {
    // The hero removes a side of the page's week whatever week was last touched.
    mutate(t('thisWeek.toastRemoved', { title: recipeIndex[meal.recipeId]?.title ?? t('thisWeek.meal') }), () =>
      dispatch(removeFromWeek(meal.id)),
    [weekStartOf(meal.scheduledFor)]);
  }, [dispatch, mutate, recipeIndex, t]);

  const handleEntryPress = useCallback((sk: number) => {
    // Entry rows never target a dish — drop any stale replace target so the
    // library pick that may follow adds instead of swapping another slot's dish.
    setReplaceTargetId(null);
    setChooser({ sk });
  }, []);

  const handleChooserPick = useCallback((key: 'recipe' | 'dining' | 'leftover' | 'note' | 'skip' | 'clear') => {
    const sk = chooser!.sk;
    setChooser(null);
    const scheduledFor = addDays(activeWeek, dayOf(sk));
    const mealType = (['breakfast', 'lunch', 'dinner'] as const)[rowOf(sk)];
    const currentMeal = slotsMap.get(sk);

    if (key === 'recipe') {
      libReplacesEntry.current = true;
      dispatch(setLibFor(sk));
    } else if (key === 'dining' || key === 'note') {
      const initial = currentMeal && isEntryMeal(currentMeal)
        ? (currentMeal.entry?.type === key ? {
          place: (currentMeal.entry as any).place,
          note: (currentMeal.entry as any).note,
          text: (currentMeal.entry as any).text,
        } : null)
        : null;
      setEntryForm({ sk, type: key, initial });
    } else if (key === 'leftover') {
      setLeftoverPick({ sk });
    } else if (key === 'skip') {
      mutate(t('thisWeek.toastNothingPlanned'), () =>
        dispatch(setSlotEntry({ entry: { type: 'skip' }, scheduledFor, mealType }))
      );
    } else if (key === 'clear') {
      mutate(t('thisWeek.toastSlotCleared'), () =>
        dispatch(clearSlot({ scheduledFor, mealType }))
      );
    }
  }, [chooser, activeWeek, dispatch, mutate, slotsMap, t]);

  const handleEntrySave = useCallback(
    (data: { place?: string; note?: string; text?: string }) => {
      const { sk, type } = entryForm!;
      setEntryForm(null);
      const scheduledFor = addDays(activeWeek, dayOf(sk));
      const mealType = (['breakfast', 'lunch', 'dinner'] as const)[rowOf(sk)];
      const entry = type === 'dining'
        ? { type: 'dining' as const, place: data.place || '', ...(data.note ? { note: data.note } : {}) }
        : { type: 'note' as const, text: data.text! };
      const undoMsg = placedInSlotMessage(
        t,
        sk,
        entry.type === 'dining' ? entry.place || t('entryForm.defaultPlace') : entry.text,
      );
      mutate(undoMsg, () => dispatch(setSlotEntry({ entry, scheduledFor, mealType })));
    },
    [entryForm, activeWeek, dispatch, mutate, t]
  );

  const handleLeftoverPick = useCallback(
    (c: LeftoverCandidate) => {
      const sk = leftoverPick!.sk;
      setLeftoverPick(null);
      mutate(placedInSlotMessage(t, sk, t('entry.leftovers')), () =>
        placeLeftover(
          c,
          addDays(activeWeek, dayOf(sk)),
          (['breakfast', 'lunch', 'dinner'] as const)[rowOf(sk)]
        )
      );
    },
    [leftoverPick, activeWeek, mutate, placeLeftover, t]
  );

  const handleFanConvert = useCallback((kind: 'dining' | 'leftover' | 'skip' | 'note') => {
    if (sel === null) return;
    closeFan();
    const scheduledFor = addDays(activeWeek, dayOf(sel));
    const mealType = (['breakfast', 'lunch', 'dinner'] as const)[rowOf(sel)];

    if (kind === 'skip') {
      mutate(t('thisWeek.toastNothingPlanned'), () =>
        dispatch(setSlotEntry({ entry: { type: 'skip' }, scheduledFor, mealType }))
      );
    } else if (kind === 'dining' || kind === 'note') {
      setEntryForm({ sk: sel, type: kind, initial: null });
    } else if (kind === 'leftover') {
      setLeftoverPick({ sk: sel });
    }
  }, [sel, activeWeek, dispatch, mutate, closeFan, t]);

  /**
   * Puts a recipe in a slot. With a target dish it swaps that one dish out
   * (removing before adding, since addToWeek refuses to go past
   * MAX_MEALS_PER_SLOT); without one it stacks a new dish onto the slot.
   * `week` names the Monday the slot key belongs to when the caller is not a
   * row of the last-touched week — the hero always acts on the page's week.
   * `replaceEntries` is the entry menu's « Recette »: the note gives way to the
   * recipe. Otherwise a note or leftovers stay, and the recipe is their side.
   */
  const placeRecipe = useCallback((
    sk: number,
    recipeId: string,
    targetMealId: string | null,
    week: string = activeWeek,
    replaceEntries = false,
  ) => {
    const recipe = recipeIndex[recipeId];
    if (!recipe) return;
    const scheduledFor = addDays(week, dayOf(sk));
    const mealType = (['breakfast', 'lunch', 'dinner'] as const)[rowOf(sk)];
    const slotMealsArray = (week === activeWeek
      ? slotMeals
      : sections.find((section) => section.weekStart === week)?.slotMeals ?? buildSlotMeals(mealsOfWeek(week))
    ).get(sk) ?? [];

    if (targetMealId) {
      // If slot has accepted meal and household is paired, propose instead of replace
      if (householdStatus === 'paired' && slotHasAcceptedMeal(slotMealsArray) && myUid) {
        dispatch(proposeMeal({
          recipe: recipe as any,
          scheduledFor,
          mealType,
          uid: myUid,
          proposesInsteadOf: targetMealId,
        }));
      } else {
        // Solo household or no accepted meal: replace normally
        const previous = visibleMeals.find((m) => m.id === targetMealId);
        mutate(
          t('thisWeek.toastReplaced', { title: recipe.title }),
          () => {
            dispatch(removeFromWeek(targetMealId));
            dispatch(addToWeek({
              recipe: recipe as any,
              scheduledFor,
              mealType,
              ...(previous?.servings != null ? { servings: previous.servings } : {}),
              // Inherit the stamp so the new dish keeps the replaced one's place.
              ...(previous?.createdAt ? { createdAt: previous.createdAt } : {}),
              // Replacing destroys the row and mints a new id, so the cook has to
              // be re-passed or the swap silently un-assigns them.
              ...(previous?.cookId ? { cookId: previous.cookId } : {}),
            }));
          }
        );
      }
    } else {
      // Resto and « rien » hold their slot alone — adding a recipe over one
      // clears it rather than hiding it. A note or leftovers are the main and
      // the recipe goes beside them, unless the cook asked to replace them.
      const entries = slotMealsArray.filter(replaceEntries ? isEntryMeal : isExclusiveEntryMeal);
      // A dish served with another feeds the same table: it takes the main's
      // portions rather than the week's default.
      const main = slotMealsArray.find((m) => isAccepted(m) && !isEntryMeal(m));
      mutate(t('thisWeek.toastAdded', { title: recipe.title }), () => {
        entries.forEach((e) => dispatch(removeFromWeek(e.id)));
        dispatch(addToWeek({
          recipe: recipe as any,
          scheduledFor,
          mealType,
          ...(main?.servings != null ? { servings: main.servings } : {}),
        }));
      }, [week]);
    }
  }, [dispatch, recipeIndex, visibleMeals, activeWeek, slotMeals, sections, mealsOfWeek, mutate, t, householdStatus, myUid]);

  const addHeroSide = useCallback((surface: 'mobile_hero' | 'mobile_hero_sheet') => {
    if (!heroSide || heroSk === null) return;
    trackEvent('side_dish_added', { surface, angle: heroSide.labelKey });
    placeRecipe(heroSk, heroSide.recipe.id, null, weekStart);
  }, [heroSide, heroSk, placeRecipe, weekStart]);

  const handleFanSelect = useCallback((recipeId: string) => {
    if (sel === null) return;
    if (fanSides) {
      const option = fanOptions.find((o) => o.recipe.id === recipeId);
      trackEvent('side_dish_added', {
        surface: 'mobile_sheet',
        angle: option?.reason?.labelKey ?? (withFoods ? 'picked_foods' : null),
      });
    } else {
      trackFanPick(recipeId);
    }
    placeRecipe(sel, recipeId, replaceTargetId);
    closeFan();
  }, [sel, fanSides, fanOptions, withFoods, trackFanPick, placeRecipe, replaceTargetId, closeFan]);

  const handleFanMove = useCallback(() => {
    if (sel === null || !fanTargetMeal) return;
    setMoveCtx({ mealId: fanTargetMeal.id, fromFan: true });
    dispatch(setMoving(true));
  }, [dispatch, sel, fanTargetMeal]);

  /** "Déplacer" on the meal card and in the recipe detail sheet (#563). */
  const openMove = useCallback((_sk: number, meal: PlannedMeal) => {
    setMoveCtx({ mealId: meal.id, fromFan: false });
    dispatch(setMoving(true));
  }, [dispatch]);

  const closeMove = useCallback(() => {
    setMoveCtx(null);
    dispatch(setMoving(false));
  }, [dispatch]);

  const handleDetailMove = useCallback(() => {
    if (!detailMeal) return;
    dispatch(setDetailFor(null));
    setDetailMealId(null);
    openMove(slotKeyOfMeal(detailMeal), detailMeal);
  }, [detailMeal, dispatch, openMove]);

  const handleFanRemove = useCallback(() => {
    if (!fanTargetMeal) return;
    const title = recipeIndex[fanTargetMeal.recipeId]?.title;
    mutate(t('thisWeek.toastRemoved', { title: title ?? t('thisWeek.meal') }), () =>
      dispatch(removeFromWeek(fanTargetMeal.id))
    );
    closeFan();
  }, [dispatch, fanTargetMeal, recipeIndex, mutate, closeFan, t]);

  /**
   * The plan sheet resolved a tap. It never decides for the user what happens
   * to a slot that was already taken, so `action` carries their choice through
   * to the dispatch — the button and the mutation cannot drift apart.
   */
  const handleMoveConfirm = useCallback((
    mealId: string,
    date: string,
    mealType: PlannedMeal['mealType'],
    action: MoveAction = 'move',
  ) => {
    const sourceMeal = visibleMeals.find((m) => m.id === mealId);
    if (!sourceMeal) return;

    const plan = buildMovePlan(sourceMeal, { scheduledFor: date, mealType }, action);
    if (!plan) return;

    const srcTitle = recipeIndex[sourceMeal.recipeId]?.title ?? t('thisWeek.thisMeal');
    // Another week's Wednesday needs its date to be identifiable — "moved to
    // Wednesday" alone would read as this week's.
    const dayName = t(dayFullKey(dayIndexOf(plan.target.scheduledFor)));
    const day = plan.crossWeek
      ? t('thisWeek.dayWithDate', {
        day: dayName,
        date: formatDayMonth(plan.target.scheduledFor, i18n.language, 'short'),
      })
      : dayName;
    // Name what actually happened. The user picked "Remplacer" — a toast that
    // says "déplacé" hides the dish that was just evicted.
    const displaced = selectMealsForDateRange(store.getState(), date, date)
      .filter((m) => m.mealType === mealType && m.id !== mealId);
    const other = displaced.find(isEntryMeal) ?? displaced[0];
    const otherTitle = other
      ? (other.entry
        ? entryTitle(other.entry, (rid) => recipeIndex[rid]?.title, t)
        : recipeIndex[other.recipeId]?.title ?? other.recipe?.title ?? t('thisWeek.thisMeal'))
      : t('thisWeek.thisMeal');

    const msg = action === 'swap'
      ? t('thisWeek.swappedWith', { recipe: srcTitle, day })
      : action === 'stack'
        ? t('thisWeek.stackedInto', { recipe: srcTitle, day })
        : action === 'replace'
          ? t('thisWeek.replacedOn', { recipe: srcTitle, other: otherTitle, day })
          : t('thisWeek.movedTo', { recipe: srcTitle, day });

    // A cross-week move rewrites two week buckets, so undo has to snapshot both
    // — restoring only the source week would leave a copy in the destination.
    mutate(msg, () => {
      if (action === 'swap') {
        dispatch(swapSlots({ a: plan.source, b: plan.target }));
      } else {
        // Replace empties the target first, so the dish lands in a free slot —
        // the one shape moveMeal never refuses.
        if (action === 'replace') dispatch(clearSlot(plan.target));
        dispatch(moveMeal({
          mealId: sourceMeal.id,
          newScheduledFor: plan.target.scheduledFor,
          newMealType: plan.target.mealType,
        }));
      }
    }, plan.affectedWeeks);
    closeMove();
    closeFan();
  }, [dispatch, visibleMeals, recipeIndex, mutate, closeMove, closeFan, t, i18n.language, store]);

  /**
   * "Planifier une 2ᵉ fois" from inside the move sheet — the dish stays where
   * it is and a second helping lands in the slot the user picked.
   */
  const handleMoveSheetAdd = useCallback((
    date: string,
    mealType: PlannedMeal['mealType'],
    action?: 'add' | 'move' | 'replace',
    fromMealId?: string,
    options?: { cooked?: boolean },
  ) => {
    if (!moveSourceMeal) return;
    const recipe = moveSourceMeal.recipe ?? recipeIndex[moveSourceMeal.recipeId];
    if (!recipe) return;
    mutate(t('thisWeek.toastAdded', { title: moveTitle }), () => {
      if (action === 'replace' && fromMealId) dispatch(removeFromWeek(fromMealId));
      dispatch(addToWeek({ recipe: recipe as any, scheduledFor: date, mealType, cooked: options?.cooked }));
    }, [getWeekStartForDate(date)]);
    closeMove();
    closeFan();
  }, [moveSourceMeal, recipeIndex, moveTitle, mutate, dispatch, closeMove, closeFan, t]);

  const handleLibSelect = useCallback((recipeId: string) => {
    const targetSk = libFor ?? sel;
    if (targetSk === null) return;
    placeRecipe(targetSk, recipeId, replaceTargetId, activeWeek, libReplacesEntry.current);
    libReplacesEntry.current = false;
    dispatch(setLibFor(null));
    closeFan();
  }, [dispatch, libFor, sel, placeRecipe, replaceTargetId, closeFan, activeWeek]);

  const handleDetailShuffle = useCallback(() => {
    if (!detailMeal) return;
    dispatch(setDetailFor(null));
    // Derive the slot from the meal itself — the detail sheet opens from a
    // plain row tap, where no slot is selected yet. The fan targets this exact
    // dish, so it swaps it rather than stacking another one on the slot.
    openFan(slotKeyOfMeal(detailMeal), detailMeal.id);
  }, [dispatch, detailMeal, openFan]);

  const handleDetailRemove = useCallback(() => {
    if (!detailMeal) return;
    const title = recipeIndex[detailMeal.recipeId]?.title;
    mutate(t('thisWeek.toastRemoved', { title: title ?? t('thisWeek.meal') }), () =>
      dispatch(removeFromWeek(detailMeal.id))
    );
    dispatch(setDetailFor(null));
  }, [dispatch, detailMeal, recipeIndex, mutate, t]);

  const handleExpandRecipe = useCallback(() => {
    if (!detailRecipe) return;
    dispatch(setDetailFor(null));
    openRecipeDetail(router, detailRecipe);
  }, [dispatch, router, detailRecipe]);

  const handleCookedToggle = useCallback(() => {
    if (!detailMeal) return;
    if (detailMeal.status === 'cooked') {
      dispatch(unmarkMealAsCooked({ mealId: detailMeal.id }));
    } else {
      dispatch(markMealAsCooked({ mealId: detailMeal.id }));
    }
  }, [dispatch, detailMeal]);

  // Open the full "Comment c'était ?" capture (stars + date + note) for the meal.
  const handleOpenCookCapture = useCallback(() => {
    if (detailMeal) setCookCaptureMeal(detailMeal);
  }, [detailMeal]);

  const handleCookCaptureSave = useCallback((entry: CookCaptureEntry) => {
    if (!cookCaptureMeal) return;
    // Anchor the cook stamp to the chosen day at noon (avoids UTC day-shift).
    const cookDate = new Date(entry.date + 'T12:00:00').toISOString();
    dispatch(markMealAsCooked({ mealId: cookCaptureMeal.id, cookedAt: cookDate, note: entry.note, noteBy: myUid ?? undefined }));
    if (entry.rating > 0) {
      // Legacy per-meal rating, so the recipe-detail cook card still shows
      // stars. The canonical per-recipe rating is NOT written here any more —
      // it is derived from the household's verdicts, which the sheet wrote as
      // they were tapped.
      dispatch(rateMeal({ mealId: cookCaptureMeal.id, rating: entry.rating }));
    }
    setCookCaptureMeal(null);
  }, [dispatch, cookCaptureMeal, myUid]);

  // A verdict answered in the sheet, then the sheet closed without saving: the
  // cook said they ate it (#865). Mark the meal cooked on the sheet's day, with
  // no note, so a note already on the meal is kept. A meal already cooked keeps
  // its own stamp.
  const handleCookCaptureDismissAnswered = useCallback(({ date }: { date: string }) => {
    if (!cookCaptureMeal) return;
    if (cookCaptureMeal.status !== 'cooked') {
      const cookDate = new Date(date + 'T12:00:00').toISOString();
      dispatch(markMealAsCooked({ mealId: cookCaptureMeal.id, cookedAt: cookDate }));
      dispatch(setToast({ text: t('cookLog.cookedOnDismiss') }));
      trackEvent('cook_capture_dismissed_cooked', { surface: 'mobile', date });
    }
  }, [dispatch, cookCaptureMeal, t]);

  const handleToastDismiss = useCallback(() => { dispatch(clearToast()); }, [dispatch]);

  /**
   * Where the list opens (#828): the next meal, never before today. The days
   * already over sit above it, a scroll up. A page of history opens at its top.
   */
  const opening = plannerOpening({
    today,
    nowMinutes: nowAnchor().nowMinutes,
    hasLead: !!nextMeal || !!captureMeal || pendingLeftovers.length > 0
      || (sectionMeals.length === 0 && !!onGenerateWeek),
    todayDrawn: !browsingPast && sectionPlan.some(
      (section) => today >= section.weekStart && today <= addDays(section.weekStart, 6),
    ),
  });

  const [extending, setExtending] = useState(false);
  /** Set after "the next 7 days" added dinners: the grocery list is asked, never told. */
  const [groceryAsk, setGroceryAsk] = useState<{ from: string; to: string; count: number } | null>(null);
  /** The evenings "the next 7 days" is filling, drawn as placeholders until their dinner comes in (#851). */
  const [arrival, setArrival] = useState<PlanArrival | null>(null);
  const reduceMotion = useReduceMotion();
  React.useEffect(() => {
    setGroceryAsk(null);
    setArrival(null);
  }, [weekStart]);

  // The dinners come in one by one, top to bottom; the last one finishes rising
  // before the arrival is forgotten.
  React.useEffect(() => {
    if (!arrival?.landed) return;
    const done = arrivalDone(arrival);
    const id = setTimeout(
      () => setArrival((a) => (a && done ? null : a && advanceArrival(a))),
      done ? ARRIVAL_RISE_MS : arrival.shown === 0 ? 0 : STAG_MS_MOBILE,
    );
    return () => clearTimeout(id);
  }, [arrival]);

  /**
   * "Planifier les 7 jours suivants" (#722): seven dinners from `extendFrom`,
   * written without replacing anything and undoable like any other edit.
   *
   * The merge reads the store at the moment of writing, not the render that
   * offered the button: generation takes a moment, and a partner's dinner
   * that lands meanwhile must survive it.
   */
  const handleExtend = useCallback(async () => {
    if (!extendFrom || !onExtendPlan || extending) return;
    const from = extendFrom;
    setExtending(true);
    setGroceryAsk(null);
    const planned = selectMealsForDateRange(store.getState(), from, addDays(from, HORIZON_DAYS - 1))
      .filter((m) => m.mealType === 'dinner')
      .map((m) => m.scheduledFor);
    setArrival(startArrival(emptyEvenings(from, planned), !reduceMotion));
    try {
      // The generator holds the main thread for seconds on an iPad. Started
      // in the tap, it ran before the frame that drew the placeholders and
      // the card's spinner, so the screen froze instead (#851).
      await afterPaint();
      const added = await onExtendPlan(from, (generated) => {
        const { writes, added: fresh } = mergeFillOnly(store.getState().mealPlan.weeks, generated);
        if (fresh.length === 0) return [];
        // In the same tick as the write, so no render draws the new dinners
        // before they know to wait their turn.
        setArrival((a) => a && landArrival(a, fresh.map((m) => m.scheduledFor)));
        mutateWithUndo(t('thisWeek.extend.added', { count: fresh.length }), () => {
          writes.forEach(([weekStartDate, meals]) => {
            dispatch(setWeeklyPlan({
              weekStartDate,
              meals,
              source: 'extend',
              addedCount: meals.filter((m) => fresh.includes(m)).length,
            }));
          });
        }, writes.map(([monday]) => monday));
        return fresh;
      });
      if (added.length > 0) {
        const to = added.reduce((last, m) => (m.scheduledFor > last ? m.scheduledFor : last), added[0].scheduledFor);
        setGroceryAsk({ from, to, count: added.length });
      } else {
        dispatch(setToast({ text: t('thisWeek.extend.nothing') }));
      }
    } finally {
      setExtending(false);
      // Nothing landed (a failure, or every evening already taken): the
      // placeholders go back to « Ajouter un souper ».
      setArrival((a) => (a && !a.landed ? null : a));
    }
  }, [extendFrom, onExtendPlan, extending, store, mutateWithUndo, dispatch, t, reduceMotion]);

  const handleGroceryAskAdd = useCallback(() => {
    if (!groceryAsk) return;
    // Keeps the list pinned where it is and stretches it to the last new
    // dinner — the days in between are planned too, and were not on it either.
    dispatch(buildGroceryList({ coversUntil: groceryAsk.to }) as any);
    setGroceryAsk(null);
    dispatch(setToast({ text: t('thisWeek.groceryAsk.done') }));
  }, [dispatch, groceryAsk, t]);

  const weekSubtitle = formatWeekRange(weekStart, i18n.language);
  const fanDi = sel !== null ? dayOf(sel) : 0;

  return (
    <View style={styles.container} testID="weekly-mobile">
      {/* Everything that scrolls or reads as page content lives in one capped,
          centred column. Without it the phone layout stretched to the full
          viewport on a tablet, which is what made the 820px iPad meal photo a
          788x150 letterbox (#632). The sheets and the toast below stay outside
          it on purpose: their scrims have to cover the whole screen, and
          BottomSheet applies the same cap to its own panel. */}
      <View style={styles.column} testID="planner-column">
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.headerTitle}>{t('navigation.myMeals')}</Text>
          {/* Since #722 the phone is one scroll: next week's dinners follow this
              week's, so the › arrow went. The way back in time stays, as a
              labelled link on the line the range used to occupy — it costs no
              vertical space. Browsing the past names the week it is on and
              offers the way home. */}
          <View style={styles.weekNav}>
            <Pressable
              style={styles.historyLink}
              onPress={() => dispatch(setCurrentWeek(shiftWeek(weekStart, -1)))}
              accessibilityRole="button"
              accessibilityLabel={t('thisWeek.previousWeeks')}
              testID="week-prev"
              hitSlop={6}
            >
              <Feather name="chevron-left" size={14} color={Paprika.textMuted} />
              <Text style={styles.headerSubtitle} testID="week-range">
                {browsingPast ? weekSubtitle : t('thisWeek.previousWeeks')}
              </Text>
            </Pressable>
            {browsingPast && (
              <Pressable
                style={styles.historyLink}
                onPress={() => dispatch(setCurrentWeek(thisMonday))}
                accessibilityRole="button"
                accessibilityLabel={t('thisWeek.backToThisWeek')}
                testID="week-today"
                hitSlop={6}
              >
                <Text style={styles.backToToday}>{t('thisWeek.backToThisWeek')}</Text>
                <Feather name="chevron-right" size={14} color={Paprika.primary} />
              </Pressable>
            )}
          </View>
        </View>
        <View style={styles.headerRightColumn}>
        <View style={styles.headerRight}>
          <Pressable
            style={styles.navBtn}
            accessibilityLabel={t('thisWeek.editSettings')}
            onPress={() => dispatch(setConfigPhase('config'))}
          >
            <Feather name="sliders" size={15} color={Paprika.textSecondary} />
          </Pressable>
          {showRegenerate && (
            // Below 380px the labelled pill overflowed the header (the cluster
            // measured 359px inside a 344px screen), so it collapses to an
            // icon — which also leaves room for the settings avatar (#531).
            <Pressable
              style={compactHeader ? styles.navBtn : styles.regenBtn}
              onPress={generateWeek}
              testID="week-regenerate"
              accessibilityLabel={t('thisWeek.regenerate')}
            >
              <Feather name="refresh-cw" size={14} color={Paprika.textSecondary} />
              {!compactHeader && <Text style={styles.regenBtnText}>{t('thisWeek.regenerate')}</Text>}
            </Pressable>
          )}
          <HeaderAvatarButton signIn={false} />
        </View>
          {/* On the subtitle line, like the week link on the left: no height added. */}
          {showRegenerate && regenerateRange && (
            <Text style={styles.regenRange} testID="week-regenerate-range" numberOfLines={1}>
              {t(compactHeader ? 'thisWeek.regenerateRangeShort' : 'thisWeek.regenerateRange', regenerateRange)}
            </Text>
          )}
        </View>
      </View>

      {/* No reading card here on purpose: on a phone the beige block filled
          most of the first screen and pushed the meals below the fold, and the
          same read-back has just been shown, in full, on the profile beat. It
          stays on desktop, where there is room beside the grid. */}

      {/* One scroll (#722). "What am I cooking?" (#579) sits right above today,
          inside the scroll, with the days already over above it (#828). It is
          never pinned over the list: on a 393px phone every fixed pixel is
          paid for by the recipe it hides. The full-size banners
          (WeekCounterBanner, WeekProgressStrip) live on in the desktop
          layouts, which have the room. */}
      <DayList
        sections={sections}
        arrival={arrival}
        recipeIndex={recipeIndex}
        heroMealId={nextMeal?.meal.id ?? null}
        onDetailPress={handleDetailPress}
        onReplacePress={(sk, meal, monday) => { setActiveWeek(monday); handleReplacePress(sk, meal); }}
        onMovePress={(sk, meal, monday) => { setActiveWeek(monday); openMove(sk, meal); }}
        onAddDishPress={(sk, monday) => { setActiveWeek(monday); handleAddDishPress(sk); }}
        onAddSidePress={(sk, monday) => { setActiveWeek(monday); handleAddSidePress(sk); }}
        onAddPress={(sk, monday) => { setActiveWeek(monday); handleAddPress(sk); }}
        onEntryPress={(sk, monday) => { setActiveWeek(monday); handleEntryPress(sk); }}
        onPortionsPress={(meal) => setPortionsFor({ mealId: meal.id })}
        onRemoveExtra={handleRemoveExtra}
        exhaustedMealTypes={exhaustedMealTypes}
        opening={opening}
        lead={
          <>
            {nextMeal ? (
              <NextMealHero
                recipe={nextMeal.recipe}
                targetDi={nextMeal.di}
                todayDi={nextMeal.todayDi}
                mi={nextMeal.mi}
                servings={nextMeal.meal.servings}
                onPress={() => handleDetailPress(nextMeal.meal)}
                onCook={() => openRecipeDetail(router, nextMeal.recipe)}
                extras={heroExtras}
                onExtraPress={(mealId) => {
                  const meal = visibleMeals.find((m) => m.id === mealId);
                  if (meal) handleDetailPress(meal);
                }}
                onRemoveExtra={(mealId) => {
                  const meal = visibleMeals.find((m) => m.id === mealId);
                  if (meal) handleRemoveExtra(meal);
                }}
                side={heroSide?.recipe ?? null}
                onAddSide={heroSide ? () => addHeroSide('mobile_hero') : undefined}
                onSidePress={heroSide ? () => {
                  trackEvent('side_dish_previewed', { surface: 'mobile_hero', angle: heroSide.labelKey });
                  setSidePreviewId(heroSide.recipe.id);
                } : undefined}
                onMoreSides={heroSk !== null && canAddDish(pageSlotMeals.get(heroSk)) ? () => {
                  setActiveWeek(weekStart);
                  handleAddSidePress(heroSk);
                } : undefined}
              />
            ) : (
              <>
                {/* A week with nothing in it says so, and offers the way out.
                    Until this existed the phone's empty state was an effect
                    that generated a plan 800ms after you landed and uploaded
                    it — see WeekEmptyState. */}
                {sectionMeals.length === 0 && onGenerateWeek && (
                  <WeekEmptyState onGenerate={onGenerateWeek} isGenerating={isGenerating} />
                )}

                {/* "Des restes ?", once per cook (#663). Driven by store state
                    rather than by the five markMealAsCooked call sites. */}
                {captureMeal && (
                  <View style={styles.counterBannerWrap}>
                    <LeftoverCapturePrompt
                      meal={captureMeal}
                      title={recipeIndex[captureMeal.recipeId]?.title ?? captureMeal.freeText ?? ''}
                    />
                  </View>
                )}

                {pendingLeftovers.length > 0 && (
                  <View style={styles.counterBannerWrap}>
                    <LeftoverPendingBanner
                      pending={pendingLeftovers}
                      resolveTitle={(id) => recipeIndex[id]?.title}
                    />
                  </View>
                )}
              </>
            )}
          </>
        }
        inline={extendFrom && (onExtendPlan || groceryAsk) ? {
          at: extendFrom,
          node: (
            <>
              {groceryAsk && (
                <GroceryAskCard
                  count={groceryAsk.count}
                  from={groceryAsk.from}
                  to={groceryAsk.to}
                  onAdd={handleGroceryAskAdd}
                  onLater={() => setGroceryAsk(null)}
                />
              )}
              {onExtendPlan && (
                <ExtendPlanCard from={extendFrom} busy={extending} onPress={handleExtend} />
              )}
            </>
          ),
        } : null}
        onNearEnd={canLoadMore ? loadNextWeek : undefined}
        footer={!canLoadMore && (
          <Text style={styles.scrollEnd} testID="planner-scroll-end">
            {t('thisWeek.scrollEnd', {
              date: formatDayMonth(addDays(sectionPlan[sectionPlan.length - 1].weekStart, 6), i18n.language, 'short'),
            })}
          </Text>
        )}
      />
      </View>

      {/* Portions adjuster sheet */}
      {portionsFor !== null && (() => {
        const meal = visibleMeals.find((m) => m.id === portionsFor.mealId);
        if (!meal) return null;
        const recipeTitle = recipeIndex[meal.recipeId]?.title ?? t('thisWeek.meal');
        return (
          <PortionsSheet
            visible
            recipeTitle={recipeTitle}
            dayLabel={t(dayFullKey(dayOf(slotKeyOfMeal(meal))))}
            servings={meal.servings}
            weekDefault={weekDefaultPortions}
            onChange={(n) => dispatch(updateMealServings({ mealId: meal.id, servings: n }))}
            onClose={() => setPortionsFor(null)}
          />
        );
      })()}

      {/* Toast */}
      {toast && (
        <Toast
          toast={toast}
          bottomOffset={84}
          onUndo={toast.undoKey ? () => {
            undo(toast.undoKey!);
            // The ask names dinners the undo may just have taken back.
            setGroceryAsk(null);
          } : undefined}
          onDismiss={handleToastDismiss}
        />
      )}

      {/* Recipe detail sheet */}
      <RecipeDetailSheet
        visible={!!detailFor && !!detailRecipe}
        recipe={detailRecipe}
        dietaryRole={detailMeal?.dietaryRole}
        isCooked={detailMeal?.status === 'cooked'}
        onClose={() => { setDetailMealId(null); dispatch(setDetailFor(null)); }}
        onRemove={handleDetailRemove}
        onShuffle={handleDetailShuffle}
        onMove={detailMeal ? handleDetailMove : undefined}
        onExpand={handleExpandRecipe}
        onCookedToggle={handleCookedToggle}
        rating={detailRecipe ? recipeMetadata[detailRecipe.id]?.rating : undefined}
        onRateRecipe={detailMeal ? handleOpenCookCapture : undefined}
      />

      {/* The hero's offered side, before it is on the menu (#854) */}
      <RecipeDetailSheet
        visible={!!sidePreview}
        recipe={sidePreview}
        onClose={() => setSidePreviewId(null)}
        onExpand={sidePreview ? () => {
          setSidePreviewId(null);
          openRecipeDetail(router, sidePreview);
        } : undefined}
        onAdd={heroSide && heroSide.recipe.id === sidePreviewId ? () => {
          setSidePreviewId(null);
          addHeroSide('mobile_hero_sheet');
        } : undefined}
      />

      {/* Cook-capture sheet — stars + planned date + note, for the selected meal */}
      {cookCaptureMeal && (
        <CookCapture
          key={cookCaptureMeal.id}
          visible
          recipeId={cookCaptureMeal.recipeId}
          defaultDate={cookCaptureMeal.scheduledFor}
          initialNote={cookCaptureMeal.status === 'cooked' ? cookCaptureMeal.cookNote : undefined}
          onClose={() => setCookCaptureMeal(null)}
          onSave={handleCookCaptureSave}
          onDismissAnswered={handleCookCaptureDismissAnswered}
          t={t}
          language={i18n.language}
        />
      )}

      {/* Fan sheet */}
      <FanSheet
        visible={sel !== null && !moving && libFor === null && chooser === null}
        di={fanDi}
        currentMealLabel={fanMealLabel}
        isEmpty={!fanTargetMeal}
        sidesFor={fanSides && sel !== null ? (recipeIndex[slotsMap.get(sel)?.recipeId ?? '']?.title ?? '') : undefined}
        sideFoods={sideFoods}
        chipBar={fanChoice.chipBar}
        isAdding={!fanTargetMeal && sel !== null && (slotMeals.get(sel)?.length ?? 0) > 0}
        canMove={!!fanTargetMeal}
        options={fanOptions}
        onSelect={handleFanSelect}
        onMove={handleFanMove}
        onRemove={handleFanRemove}
        onConvert={handleFanConvert}
        onLibrary={() => {
          libReplacesEntry.current = false;
          dispatch(setLibFor(sel ?? 0));
        }}
        onClose={closeFan}
      />

      {/* Move: the same plan sheet the recipe detail uses, so a move is one
          act with one vocabulary wherever it starts. */}
      {moveCtx && moveSourceMeal && (
        <AddToMealPlanModal
          visible={moving}
          recipeId={String(moveSourceMeal.recipeId)}
          recipeTitle={moveTitle}
          mode="move"
          movingMealId={moveSourceMeal.id}
          onMove={handleMoveConfirm}
          onRemove={(id) => {
            mutate(
              t('thisWeek.toastRemoved', { title: moveTitle }),
              () => dispatch(removeFromWeek(id)),
            );
            closeMove();
            closeFan();
          }}
          onAdd={handleMoveSheetAdd}
          onClose={() => { closeMove(); closeFan(); }}
        />
      )}

      {/* Library sheet */}
      <LibrarySheet
        visible={libFor !== null}
        entries={recipeEntries}
        placingFor={libFor}
        weekStart={activeWeek}
        onSelect={handleLibSelect}
        onClose={() => {
          libReplacesEntry.current = false;
          dispatch(setLibFor(null));
        }}
      />

      {/* Chooser sheet */}
      {chooser && (
        <ChooserSheet
          visible={chooser !== null}
          hasContent={!!slotsMap.get(chooser.sk)}
          dayLabel={slotLabel(t, chooser.sk)}
          onPick={handleChooserPick}
          onClose={() => setChooser(null)}
        />
      )}

      {/* Entry form sheet */}
      {entryForm && (
        <EntryFormSheet
          visible={entryForm !== null}
          type={entryForm.type}
          initial={entryForm.initial}
          dayLabel={slotLabel(t, entryForm.sk)}
          onSave={handleEntrySave}
          onCancel={() => setEntryForm(null)}
        />
      )}

      {/* Leftover sheet */}
      {leftoverPick && (
        <LeftoverSheet
          visible={leftoverPick !== null}
          fridge={leftoverFridge}
          freezer={leftoverFreezer}
          today={today}
          dayLabel={slotLabel(t, leftoverPick.sk)}
          resolve={(id) => recipeIndex[id]}
          onPick={handleLeftoverPick}
          onClose={() => setLeftoverPick(null)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  column: {
    flex: 1,
    width: '100%',
    maxWidth: PaprikaLayout.contentMax,
    alignSelf: 'center',
  },
  // Twelve weeks ahead is where the scroll stops adding weeks (#769).
  scrollEnd: {
    fontFamily: 'Inter-Regular',
    fontSize: 12.5,
    lineHeight: 18,
    color: Paprika.textMuted,
    textAlign: 'center',
    paddingHorizontal: 32,
    paddingTop: 24,
  },
  counterBannerWrap: {
    marginHorizontal: 16,
    marginTop: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.surfaceLow,
  },
  headerLeft: {
    gap: 2,
  },
  headerTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 22,
    color: Paprika.textPrimary,
  },
  headerSubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
    // No textTransform: 'capitalize' — it made « Semaines Précédentes » and
    // « 28 Septembre »; French does not capitalise either, English already does.
  },
  // The week control sits on the subtitle line, so it costs no vertical space.
  weekNav: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    marginLeft: -6,
  },
  historyLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingVertical: 4,
    paddingRight: 6,
  },
  backToToday: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    color: Paprika.primary,
  },
  headerRight: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  headerRightColumn: {
    alignItems: 'flex-end',
    gap: 2,
  },
  regenRange: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
  },
  navBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: Paprika.surfaceLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  navBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 18,
    color: Paprika.textSecondary,
  },
  regenBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    borderRadius: 100,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: Paprika.surfaceLowest,
  },
  regenBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    fontWeight: '500' as const,
    color: Paprika.textSecondary,
  },
});
